import { toast } from "@/components/Toast"
import { Button, Text } from "@/elements"
import { graphql } from "@/gql"
import { PollForProSubscriptionDocument, SyncUserSubscriberDocument } from "@/gql/graphql"
import { useSession } from "@/hooks/useSession"
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import { PaymentElement, useCheckout } from "@stripe/react-stripe-js"
import { useState } from "react"

interface Props {
  setStep: (step: number) => void
  setIsModalLocked: (canModalClose: boolean) => void
}

graphql(`
  query PollForProSubscription($creativeId: ID!) {
    individualCreative(id: $creativeId) {
      id
      __typename
      proMembership {
        id
        activeMembershipType
      }
    }
  }
`)

export const CheckoutForm: React.FC<Props> = ({ setStep, setIsModalLocked }) => {
  const { confirm, total } = useCheckout()
  const [processing, setProcessing] = useState(false)
  const [syncSubscription] = useMutation(SyncUserSubscriberDocument)
  const apolloClient = useApolloClient()
  const session = useSession()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  /* stripe requires the total.total.amount to be read before the form is submitted */
  total?.total.amount
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (processing) return
    setErrorMessage(null)
    try {
      setProcessing(true)
      setIsModalLocked(true)
      const f = await confirm({
        redirect: "if_required",
        returnUrl: `${window.location.origin}/settings/membership`,
      })

      if (f.type === "error") {
        if (["You already have a subscription with us. Manage your subscription instead."].includes(f.error.message)) {
          // continue
          // we maybe need to make a call to stripe here to confirm that the subscription is active
          // if there are other error messages which suggest this like:
          // "Your payment has already been processed."
        } else {
          setProcessing(false)
          setErrorMessage(f.error.message)
          setTimeout(() => {
            window.document.getElementById("payment-button")?.scrollIntoView({ behavior: "smooth" })
          }, 0)
          captureException(f.error)
          console.error(f.error)
          return
        }
      }

      const checkMembership = async () => {
        const { data } = await apolloClient.query({
          query: PollForProSubscriptionDocument,
          variables: { creativeId: session?.data?.user.creative.id! },
          fetchPolicy: "no-cache",
        })
        return data?.individualCreative?.proMembership?.activeMembershipType
      }

      const startPollingCycle = async (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
          let elapsed = 0
          const interval = 750
          const timeout = 15000
          const timer = setInterval(async () => {
            elapsed += interval
            const membershipType = await checkMembership()
            if (membershipType) {
              clearInterval(timer)
              resolve(true)
            } else if (elapsed >= timeout) {
              clearInterval(timer)
              resolve(false)
            }
          }, interval)
        })
      }

      // First sync and poll
      await syncSubscription({ variables: { id: session?.data?.user.id! } })
      const firstPoll = await startPollingCycle()

      if (firstPoll) {
        await apolloClient.reFetchObservableQueries()
        setStep(3)
        // User shouldn't see this happen so use timeout
        setTimeout(() => setProcessing(false), 1000)
        return
      }

      // Second sync and poll
      await syncSubscription({ variables: { id: session?.data?.user.id! } })
      const secondPoll = await startPollingCycle()

      if (secondPoll) {
        await apolloClient.reFetchObservableQueries()
        setStep(3)
        // User shouldn't see this happen so use timeout
        setTimeout(() => setProcessing(false), 1000)
      } else {
        setProcessing(false)
        toast.error("Subscription activation timeout. Please contact support.")
        captureException(new Error("Subscription activation timeout"))
      }
    } catch (error) {
      setProcessing(false)
      captureException(error)
      console.error(error)
    } finally {
      setIsModalLocked(false)
    }
  }

  return (
    <form className="flex h-full flex-col justify-between space-y-6" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-y-5">
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
            paymentMethodOrder: ["card", "apple_pay", "google_pay"],
          }}
        />
      </div>
      {errorMessage ? <Text className="text-red-dark">{errorMessage}</Text> : null}
      <div className="mt-auto" id="payment-button">
        <Button type="submit" disabled={processing} className="w-full">
          {processing ? "Processing..." : "Purchase"}
        </Button>
      </div>
    </form>
  )
}
